import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useLocale } from '@va/localization';
import { TooltipOptions } from '@va/types/tooltip';
import { TableComponent } from '@va/ui/components/data-table';
import { fontWeights, ListCardItemWithIcon, Paragraph, paragraphSizes, ProgressBar } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { addNumberSeparator, getPercentageColors } from '@va/util/helpers';
import classNames from 'classnames';
import { FC, ReactNode, useMemo } from 'react';

export type ProgressListCardItem = {
  icon: ReactNode;
  title: ReactNode;
  titleTooltip?: ReactNode;
  percentageChange?: number;
  percentage: number;
  countLabel: string;
  count: number;
  progress: {
    color: string;
    percentage: number;
  }[];
  progressBarTooltip?: ReactNode;
  progressBarTooltipProps?: TooltipOptions;
  color?: string;
  combinePercentages?: boolean;
};

type ProgressListCardContentProps = {
  items: ProgressListCardItem[];
  isLoading?: boolean;
  selectedRowIndex?: number | null;
  onMouseEnter?: (index: number) => void;
  onMouseLeave?: () => void;
  placeholdersCount?: number;
};
export const ProgressListCardContent: FC<ProgressListCardContentProps> = ({
  items,
  isLoading,
  placeholdersCount,
  onMouseEnter,
  onMouseLeave,
}) => {
  const columns = useColumns();

  const table = useReactTable({
    columns: columns,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    enableMultiRowSelection: false,
    initialState: {
      pagination: {
        pageSize: placeholdersCount,
      },
    },
  });

  return (
    <TableComponent
      table={table}
      isLoading={isLoading}
      displayTableHeader={false}
      onRowMouseEnter={(row) => {
        onMouseEnter?.(row.index);
      }}
      onRowMouseLeave={() => {
        onMouseLeave?.();
      }}
      style={{
        // VISA-10872 - there is an issue with displaying table columns as grid items on Firefox
        gridTemplateColumns: 'minmax(200px, 280px) minmax(100px, 1fr) minmax(150px, 180px)',
      }}
    />
  );
};

const columnHelper = createColumnHelper<ProgressListCardItem>();
const useColumns = () => {
  const { locale } = useLocale();
  return useMemo(
    () => [
      // @ts-ignore
      columnHelper.accessor('title', {
        meta: {
          useDefaultCellStyle: false,
          cellClassName: 'justify-start',
        },
        cell: ({ row }) => {
          const { title, titleTooltip, icon, percentageChange } = row.original;
          return (
            <TooltipWrapper content={titleTooltip} interactive disabled={!titleTooltip}>
              <div>
                <ListCardItemWithIcon
                  bottomNode={
                    !!percentageChange && (
                      <Paragraph
                        size={paragraphSizes.tiny}
                        className={classNames('font-525!', getPercentageColors(percentageChange))}
                      >
                        {addNumberSeparator(percentageChange, locale, {
                          style: 'percent',
                          signDisplay: 'exceptZero',
                        })}
                      </Paragraph>
                    )
                  }
                  topText={title}
                  icon={icon}
                />
              </div>
            </TooltipWrapper>
          );
        },
      }),
      columnHelper.accessor('progress', {
        cell: ({ getValue, row }) => {
          const { progressBarTooltip, progressBarTooltipProps, percentage, color, combinePercentages } = row.original;

          return (
            <TooltipWrapper
              {...progressBarTooltipProps}
              interactive={true}
              disabled={!progressBarTooltip}
              content={progressBarTooltip}
            >
              <div className='w-full'>
                <ProgressBar
                  progress={
                    combinePercentages
                      ? [
                          {
                            percentage: percentage,
                            color: color ?? window.PRIMARY_COLOR,
                          },
                        ]
                      : getValue()
                  }
                />
              </div>
            </TooltipWrapper>
          );
        },
      }),
      columnHelper.accessor('percentage', {
        meta: {
          useDefaultCellStyle: false,
          cellClassName: 'justify-end',
        },
        cell: ({ row }) => {
          const { percentage, countLabel } = row.original;

          return (
            <div className={'text-right'}>
              <Paragraph weight={fontWeights.medium}>
                {addNumberSeparator(percentage, locale, { style: 'percent', signDisplay: 'never' })}
              </Paragraph>
              <Paragraph weight={fontWeights.medium} size={paragraphSizes.tiny}>
                {countLabel}
              </Paragraph>
            </div>
          );
        },
      }),
    ],
    [locale],
  );
};
